import React, { useEffect } from 'react';
import { Client, HydrationProvider } from 'react-hydration-provider';
import Select from 'react-select';

import { mergeUrlParams } from '../../tools/textTools';
import { AutoPauseVideo } from '../Generic/AutoPauseVideo';
import PageSection from '../PageSections/PageSection';
import ContentfulImage from './ContentfulImage';
import { ArrowLink } from './Links';
import RichTextConverter from './RichText/RichTextConverter';
import * as styles from './TabbedContent.module.scss';

const Tabs = ({ selectTab, selectedTab = 0, menuItems = [] }) => {
  const tabColor = styles[menuItems[selectedTab]?.backgroundColor || 'white'];
  const placeholderOverrides = {
    Evergreen: '#135050',
    Pink: '#ff2059',
    Lilac: '#9e36f1',
  };
  return (
    <>
      {/* MOBILE */}
      <div className={styles.tabContainerMobile}>
        <HydrationProvider>
          <Client>
            <Select
              styles={{
                placeholder: (baseStyles) => ({
                  ...baseStyles,
                  color: placeholderOverrides[menuItems[selectedTab]?.backgroundColor],
                }),
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: placeholderOverrides[menuItems[selectedTab]?.backgroundColor],
                }),
                indicatorSeparator: (baseStyles) => ({
                  ...baseStyles,
                  backgroundColor: placeholderOverrides[menuItems[selectedTab]?.backgroundColor],
                }),
                dropdownIndicator: (baseStyles) => ({
                  ...baseStyles,
                  color: placeholderOverrides[menuItems[selectedTab]?.backgroundColor],
                }),
              }}
              isSearchable={false}
              outlined={true}
              className={styles.dropDown}
              options={menuItems.map((item, i) => {
                return {
                  label: item.title,
                  value: i,
                };
              })}
              placeholder={menuItems[selectedTab]?.title}
              value={selectedTab}
              onChange={(e) => {
                selectTab(e.value);
              }}
            />
          </Client>
        </HydrationProvider>
      </div>

      {/* DESKTOP */}
      <div className={styles.tabs} style={{ '--tab-count': menuItems.length }}>
        <div className={[styles.pill, tabColor].join(' ')} style={{ '--tab-index': selectedTab }} />
        {menuItems.map((item, index) => {
          return (
            <button
              key={index}
              className={[styles.tab, index === selectedTab ? styles.activeTab : ''].join(' ')}
              onClick={() => selectTab(index)}
              data-analytics-component={'data-analytics-tabbedcontent-tab'}
              data-analytics-id={item.title}
            >
              {item.logo && <ContentfulImage imageFragment={item.logo} className={styles.logo} />}
              {item.title}
            </button>
          );
        })}
      </div>
    </>
  );
};

const TabbedContent = ({ heading, lede, content = [] }) => {
  const [selectedTab, selectTab] = React.useState(0);
  const [searchQuery, setSearchQuery] = React.useState('');
  const backgroundColor = styles[content[selectedTab]?.backgroundColor || 'white'];

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search) {
      setSearchQuery(window.location.search);
    }
  }, []);

  return (
    <PageSection className={[styles.section, backgroundColor].join(' ')}>
      <div className={styles.heading}>
        <h2>{heading}</h2>
        {lede && <p>{lede}</p>}
      </div>
      <div className={styles.content}>
        <Tabs selectTab={selectTab} selectedTab={selectedTab} menuItems={content} />
        <div className={styles.tabContent}>
          {content.map((item, index) => {
            let url = item?.ctaUrl;
            url = mergeUrlParams(url, searchQuery);

            return (
              <div key={index} className={selectedTab === index ? styles.tabContainer : styles.hidden}>
                <div>
                  <RichTextConverter copy={item.copy} />
                  {item.ctaUrl && item.ctaLabel && <ArrowLink label={item.ctaLabel} url={url} />}
                </div>
                <div>
                  {item.mainImage && <ContentfulImage lazy={true} imageFragment={item.mainImage} />}
                  {item.wistiaUrl && <AutoPauseVideo src={item.wistiaUrl} />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </PageSection>
  );
};

export default TabbedContent;
